// Distinct app sessions since the founder's profile crossed the completeness
// gate. Surfaced to Intercom as `sf_sessions_since_completion`, which the
// PMF-pulse audience filters on — "has come back at least once" is the part of
// that rule no server-side signal can see, because nothing about a returning
// visit reaches our API.
//
// Device-local, like the other engagement counts. Intercom's own "once per
// person" frequency owns whether the survey actually fires, so a count that
// only one of a founder's devices reached is still enough: once any device
// crosses the bar, the founder is asked once, account-wide.

/** A gap this long ends the current session and starts a new one. */
export const SESSION_IDLE_MS = 30 * 60 * 1000

const sessionsKey = (userId: string) => `sf_sessions_${userId}`

/**
 * Scoped to a completion stamp: if `profile_completed_at` moves — an admin
 * unsets it, the founder drops back under the gate and crosses it again — the
 * count restarts from the new stamp rather than carrying sessions that happened
 * before the value loop closed.
 */
interface SessionState {
  completedAt: string
  count: number
  lastSeenAt: number
  startedAt: number
}

function read(key: string): SessionState | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as SessionState) : null
  } catch {
    // Private mode, a quota wall, or something else wrote garbage under this
    // key. A missing count beats a home page that throws.
    return null
  }
}

/**
 * Records that the founder is active now and returns the session it belongs to.
 *
 * Call on mount and on a heartbeat while the tab is visible. A gap longer than
 * `SESSION_IDLE_MS` counts as a new session — the standard analytics
 * definition, chosen because it is the one anyone reading the number assumes.
 */
export function touchSession(
  userId: string,
  profileCompletedAt: number | null,
  now: number
): number {
  // Before completion there is nothing to count sessions since.
  if (profileCompletedAt === null) return 0

  const completedAt = new Date(profileCompletedAt).toISOString()
  const stored = read(sessionsKey(userId))
  const sameCompletion = stored?.completedAt === completedAt
  const carriesOver = stored !== null && sameCompletion && now - stored.lastSeenAt < SESSION_IDLE_MS

  const next: SessionState = carriesOver
    ? { ...stored, lastSeenAt: now }
    : {
        completedAt,
        count: (sameCompletion ? (stored?.count ?? 0) : 0) + 1,
        lastSeenAt: now,
        startedAt: now,
      }

  try {
    localStorage.setItem(sessionsKey(userId), JSON.stringify(next))
  } catch {
    // Storage unavailable: the count stops advancing rather than breaking Home.
  }
  return next.count
}
